import {
	Component,
	ComponentFactoryResolver,
	ElementRef,
	OnInit,
	ViewChild,
} from '@angular/core';
import { AfterViewInit } from '@angular/core';
import * as he from 'he';
import moment from 'moment';
import {
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { _, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { AppConfig } from '../../app.config';
import { DatastoreService } from '../../services/datastore.service';
import { DobValidatorService } from '../../services/dob-validator.service';
import { ApiService } from '../../services/jrni/api.service';
import { AvailabilityService } from '../../services/jrni/availability.service';
import { BasketService } from '../../services/jrni/basket.service';
import { DepartmentService } from '../../services/jrni/department.service';
import { MemberService } from '../../services/jrni/member.service';
import { ServicesService } from '../../services/jrni/services.service';
import { AlertService } from '../../_alert';
import { ConfigService } from '../../services/config.service';
import { confirmPasswordValidator } from '../../validators/confirm-password.validator';
import { passwordValidator } from '../../validators/password.validator';
import { postcodeValidator } from "../../validators/postcode.validator";
import { CostBreakdownComponent } from '../../cost-breakdown/cost-breakdown.component';
import { GovukInitService } from '../../services/govuk-init.service';
import { PasswordInput } from 'govuk-frontend';
import { inject } from '@angular/core';
import { Question } from '../../fees-and-make-it-yours-enhancements/fees-and-make-it-yours-enhancements.component';
import { B2cService } from '../../services/b2c.service';
import { ExtendedJwtPayload } from '../../models/ExtendedJwtPayload.model';
import { CommonFunctionsService } from '../../services/common-functions.service';
import { FormBuilderComponent } from '@bookinglab/ui';
import { CommonModule } from '@angular/common';
import { PageHeadingComponent } from '@bookinglab/govbook-ui/layout';
import {
	InfoBoxComponent,
	WarningBoxComponent,
} from '@bookinglab/westminster-ui';
import { BreadcrumbComponent, StepperComponent } from '@bookinglab/ui';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { BackLinkComponent } from '@bookinglab/govbook-ui/layout';
import { BookingSummaryComponent } from '../../booking-summary/booking-summary.component';
import { NgIf, NgFor } from '@angular/common';
import { FeesAndMakeItYoursEnhancementsComponent } from '../../fees-and-make-it-yours-enhancements/fees-and-make-it-yours-enhancements.component';

// BRPO-31: names were only `required` and the phone used an unanchored
// /[0-9]/. Name pattern is permissive (O'Brien, Anne-Marie, van der Berg pass;
// digits fail); phone uses the per-council validation.phone regex.
const NAME_PATTERN =
	/^[A-Za-z\u00C0-\u024F\u1E00-\u1EFF][A-Za-z\u00C0-\u024F\u1E00-\u1EFF'’\-. ]*$/;

// Fallback when config has no validation.phone.
const DEFAULT_PHONE_PATTERN = /^(\+\d{1,3}[- ]?)?\d{9,15}$/;

@Component({
	selector: 'app-booking-details',
	standalone: true,
	templateUrl: './booking-details.component.html',
	styleUrls: ['./booking-details.component.scss'],
  imports: [
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      PageHeadingComponent,
      TranslatePipe,
      InfoBoxComponent,
      WarningBoxComponent,
      StepperComponent,
      // FormErrorMessagesComponent,
      MatAutocompleteModule,
      FormBuilderComponent,
      BreadcrumbComponent,
      BackLinkComponent,
      BookingSummaryComponent,
      NgIf,
      NgFor,
      FeesAndMakeItYoursEnhancementsComponent
    ],
})
export class BookingDetailsComponent implements OnInit, AfterViewInit {
	selectedDepartment;
	selectedService;
	pageTitle: string;
	dataPolicy: string;
	tncPolicy: string;
	servicePolicy: string;
	dataPolicyCy: string;
	bookingDetailsPageText: string;
	bookingQuestions = [];
	submitted: boolean;
	selectedLocation;
	requiresFullRegistration = false;
	showBookingQuestions = false;
	organisationQuestions = [];
	today = new Date();
	selectedFile: string;
	selectedId: any;
	subtmitButtonDisabled: boolean;
	addresses = [];
	addressFoundBefore = false;
	member;
	slot;
	banned = false;
	customerQuestions;
	unavailableTimes = [];
	currentYear = new Date().getFullYear();
	// https://regexr.com/3pp3r <- Postcode validator
	postcodeValidator = new RegExp(
		'^(([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))s?[0-9][A-Za-z]{2}))$'
	);
	dateId;
	costBreakDown = [];
	companyQuestions = [];
	searchText: string;
	debounceTimer: any;
	adminJourney = false;
	enhancedCostBreakdownQuestions: Question[] = [];
	tokenData: ExtendedJwtPayload;
	tokenValid: boolean;
	showContactForm = false;
	bookingAnswers = [];
	selectedResource;
	continueGuidance = '';
	noticeGuidance = '';
	showFormBuilder = true;
	formData: any = [];
	selectedFormId = '';

	breadcrumbItems = [
		{ text: 'Home', href: '/start' },
		{ text: 'Ceremony Selection', href: '/services' },
		{ text: 'Date & Time', href: '/calendar' },
		{ text: 'Booking Details', href: '/booking-details' },
	];

	@ViewChild('password') passwordCtrl!: ElementRef;

	// SUP-2485: schema form ref so onSubmit() can enforce its required fields.
	@ViewChild(FormBuilderComponent) formBuilderRef?: FormBuilderComponent;

	// Initialise the customer form
	customerDetailsForm: UntypedFormGroup = new UntypedFormGroup({
		first_name: new UntypedFormControl('', [
			Validators.required,
			Validators.pattern(NAME_PATTERN),
		]),
		last_name: new UntypedFormControl('', [
			Validators.required,
			Validators.pattern(NAME_PATTERN),
		]),
		email_address: new UntypedFormControl('', [
			Validators.required,
			Validators.pattern(/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/i),
		]),
		email_address_consent: new UntypedFormControl('', Validators.requiredTrue),
		contact_number: new UntypedFormControl('', [
			Validators.required,
			Validators.minLength(9),
			Validators.maxLength(15),
			// Overwritten in ngOnInit with config.validation.phone (BRPO-31).
			Validators.pattern(DEFAULT_PHONE_PATTERN),
		]),
		contact_number_consent: new UntypedFormControl('', Validators.requiredTrue),
		// postcode: new FormControl(""),
		// selectedAddress: new FormControl(""),
		dob_day: new UntypedFormControl(false, [
			Validators.required,
			Validators.min(1),
			Validators.max(31),
		]),
		dob_month: new UntypedFormControl(false, [
			Validators.required,
			Validators.min(1),
			Validators.max(12),
		]),
		dob_year: new UntypedFormControl(false, Validators.required),
		password: new UntypedFormControl(''),
		copyData: new UntypedFormControl('No'),
		// confirm_password: new FormControl('', [Validators.required, confirmPasswordValidator]),
	});
	config: any;
	agentMode: any;
	appConfig;

	formBuilder: UntypedFormBuilder = inject(UntypedFormBuilder);
	b2cService: B2cService = inject(B2cService);
	commonFunctions: CommonFunctionsService = inject(CommonFunctionsService);
	constructor(
		private router: Router,
		private servicesService: ServicesService,
		private departmentService: DepartmentService,
		private basketservice: BasketService,
		private alertService: AlertService,
		private translateService: TranslateService,
		private memberService: MemberService,
		public datastoreService: DatastoreService,
		private spinner: NgxSpinnerService,
		private apiService: ApiService,
		private availabilityService: AvailabilityService,
		private configService: ConfigService,
		public translate: TranslateService,
		private govuk: GovukInitService
	) {
		this.appConfig = this.apiService.getAppConfig();
	}

	companyQuestionsForm: UntypedFormGroup = this.formBuilder.group({});
	bookingQuestionsForm: UntypedFormGroup = this.formBuilder.group({});

	pad(num: number, size: number): string {
		let s = num + '';
		while (s.length < size) s = '0' + s;
		return s;
	}

	get form() {
		return this.customerDetailsForm.controls;
	}

	get bookingForm() {
		return this.companyQuestionsForm.controls;
	}

	routerGoBack() {
		this.router.navigate(['/calendar']);
	}

	restartJourney() {
		this.router.navigate(['']);
	}

	ngOnInit() {
		this.spinner.show();
		this.govuk.init();
		this.scrollToTop();
		this.selectedFormId = this.datastoreService.selectedSchema;
		
		this.agentMode = localStorage.getItem('agent');

		console.log('admin', this.datastoreService.adminJourney);
		if (!this.datastoreService.adminJourney) {
			this.adminJourney = false;
			this.customerDetailsForm.controls['password'].setValidators([
				Validators.required,
				passwordValidator,
			]);
			console.log(this.customerDetailsForm.controls['password']);
		} else {
			this.adminJourney = true;
		}

		// restore the costbreakdown from datastore
		if (this.datastoreService.serviceFees) {
			this.costBreakDown = this.datastoreService.serviceFees;
		} else {
			this.costBreakDown = []; // clear cost breakdown; in future if we add to this more we dont want to clear but replace
		}

		this.availabilityService.getSelectedSlot().then((slot) => {
			if (slot) {
				this.slot = slot;
			}
		});
		console.log('Booking details');
		this.departmentService.get().then(async (selectedDepartment) => {
			console.log(selectedDepartment);
			this.config = this.datastoreService.config;
			this.applyConfiguredPhoneValidation();

			if (selectedDepartment === undefined) {
				this.restartJourney();
			} else {
				this.selectedDepartment = selectedDepartment;

				let registrationOption = '';
				let organisationQuestionsID;

				// Set the title on the login page to the title from the business question
				this.selectedDepartment.extra?.hasOwnProperty(
					'booking_details_page_title'
				)
					? (this.pageTitle =
						this.selectedDepartment.extra.booking_details_page_title)
					: (this.pageTitle =
						this.translateService.instant('SUMMARY.BOOKING_DETAILS_TITLE'));

				// Get the configurable data
				if (this.selectedDepartment.hasOwnProperty('extra')) {
					// Set the extra text on the page to the text taken from the business question

					if (this.adminJourney) {
						this.dataPolicy = `Advise the customer that they can see our Privacy Policy for details on how the council processes their information. They can find the policy here - <a href="/tbc" target="_blank">here</a>.`;
					} else {
						this.selectedDepartment.extra.hasOwnProperty(
							'privacy_statement_text'
						)
							? (this.dataPolicy =
								this.selectedDepartment.extra.privacy_statement_text)
							: (this.dataPolicy = 'data policy not set');

						this.selectedDepartment.extra.hasOwnProperty(
							'privacy_statement_text_welsh'
						)
							? (this.dataPolicyCy =
								this.selectedDepartment.extra.privacy_statement_text_welsh)
							: (this.dataPolicyCy = 'Welsh data policy not set');
					}

					if (this.adminJourney) {
						this.tncPolicy =
							'Registration information is retained indefinitely as required by law. Information collected during the booking of appointments and ceremonies, including certificate orders, is retained for 2 years from the date of the appointment or ceremony.';
					} else {
						this.tncPolicy =
							'Registration information is retained indefinitely as required by law. Information collected during the booking of appointments and ceremonies, including certificate orders, is retained for 2 years from the date of the appointment or ceremony.';
					}

					if (this.adminJourney) {
						this.servicePolicy =
							"The Terms and Conditions are available at: <a href='https://www.essex.gov.uk/marriage-or-civil-partnership'target='_blank'>https://www.essex.gov.uk/marriage-or-civil-partnership</a> .  They will also be included in the booking email that will be sent to the customer after the booking process has been completed.  ";
					} else {
						this.servicePolicy =
							"Please read the Essex Registration Service <a href='https://res.cloudinary.com/bookinglab/image/upload/v1742564813/Ceremony_-_Terms_and_Conditions_k5a0j7.pdf' target='_blank'>Terms and Conditions</a> and confirm you agree.";
					}

					this.selectedDepartment.extra.hasOwnProperty(
						'booking_details_page_text'
					)
						? (this.bookingDetailsPageText =
							this.selectedDepartment.extra.booking_details_page_text)
						: null;

					// Set the extra text on the page to the text taken from the business question
					this.selectedDepartment.extra.hasOwnProperty(
						'booking_details_page_text'
					)
						? (this.bookingDetailsPageText =
							this.selectedDepartment.extra.booking_details_page_text)
						: null;
					// Get the registration option
					this.selectedDepartment.extra.hasOwnProperty('registration_option')
						? (registrationOption =
							this.selectedDepartment.extra.registration_option)
						: null;
					// Get the organistion customer questions
					this.selectedDepartment.extra.hasOwnProperty(
						'organisations_questions'
					)
						? (organisationQuestionsID =
							this.selectedDepartment.extra.organisations_questions)
						: null;

					// If the register option is set to complete a full registration then capture the following details
					if (
						registrationOption.toLowerCase() === 'register complete details'
					) {
						this.requiresFullRegistration = true;
					}
				}

				if (this.datastoreService.customerDetailsForm) {
					Object.keys(
						this.datastoreService.customerDetailsForm.controls
					).forEach((key) => {
						if (this.customerDetailsForm.controls[key]) {
							this.customerDetailsForm.controls[key].setValue(
								this.datastoreService.customerDetailsForm.controls[key].value
							);
						}
					});
				}

				// Use this.departmentService.getLocation() if the services are set on the children departments
				// Use this.departmentService.get() if the services are set on the parent department
				this.departmentService.getLocation().then(async (location) => {
					this.selectedLocation = location;

					this.availabilityService.getSelectedResource().then((resource) => {
						if (resource) {
							this.selectedResource = resource;
						}
					});

					this.memberService.getMember().then((member) => {
						console.log('member', member);
						this.member = member;

						if (this.datastoreService.config.hasOwnProperty('journey_config')) {
							if (
								!this.datastoreService.config.journey_config.hasOwnProperty(
									'b2c'
								) ||
								this.datastoreService.adminJourney
							) {
								this.showContactForm = true;
								this.departmentService
									.getCustomerQuestions()
									.then((questions) => {
										const filteredQuestions = [];
										for (let index = 0; index < questions.length; index++) {
											const question = this.customerQuestions[index];
											filteredQuestions.push(question);
											question.visible = false;
											question.reference = question.id;
											if (!question.admin_only) {
												if (
													!question.settings.hasOwnProperty(
														'conditional_question'
													)
												) {
													question.visible = true;
												}

												const questionControl = question.id;
												if (
													question.detail_type == 'check' ||
													question.detail_type == 'check-price'
												) {
													if (question.default == 1) {
														if (question.required) {
															this.customerDetailsForm.addControl(
																questionControl,
																new UntypedFormControl(
																	true,
																	Validators.requiredTrue
																)
															);
														} else {
															this.customerDetailsForm.addControl(
																questionControl,
																new UntypedFormControl(true)
															);
														}
													} else {
														if (question.required) {
															this.customerDetailsForm.addControl(
																questionControl,
																new UntypedFormControl(
																	'',
																	Validators.requiredTrue
																)
															);
														} else {
															this.customerDetailsForm.addControl(
																questionControl,
																new UntypedFormControl(false)
															);
														}
													}
												} else {
													// Need to add the control for display purposes but we don't need to validate it as we've added three new controls.
													if (
														question.id !== 1 &&
														question.id !== 279 &&
														question.id !== 34
													) {
														if (question.required) {
															this.customerDetailsForm.addControl(
																questionControl,
																new UntypedFormControl('', Validators.required)
															);
														} else {
															this.customerDetailsForm.addControl(
																questionControl,
																new UntypedFormControl('')
															);
														}
													} else {
														this.customerDetailsForm.addControl(
															questionControl,
															new UntypedFormControl('')
														);
													}
												}
											}
											// }
										}
										this.customerQuestions = filteredQuestions;
										this.datastoreService.customerQuestions = filteredQuestions;
										this.dateId = '';
										// id of date question is one of these, but we don't know which
										if (this.customerDetailsForm.get('1') !== undefined)
											this.dateId = '1';
										if (this.customerDetailsForm.get('279') !== undefined)
											this.dateId = '279';
										if (this.customerDetailsForm.get('34') !== undefined)
											this.dateId = '34';
									});
							}
						}

						// Get the customer questions
					});
				});

				// Get the selected service
				this.servicesService.get().then(async (service) => {
					this.selectedService = service;

					// Get the questions
					// const res = await this.selectedService.$get('questions');
					// this.datastoreService.bookingQuestions = res.questions;
					// this.addQuestionsToForm(res.questions);

					// const questions = []
					// const sortedQuestions = res.questions;
					// for (let index = 0; index < res.questions.length; index++) {
					//   // ignore the previously asked questions
					//   const question = sortedQuestions[index];
					//   if ((this.config['first_page_questions_to_ignore'].includes(question.id))) {
					//     questions.push(question)
					//     if (![275, 276, 277, 278].includes(question.id)) {
					//       question.visible = false;
					//       question.reference = question.id;
					//       if (!question.admin_only) {
					//         if (!question.settings.hasOwnProperty("conditional_question")) {
					//           question.visible = true;
					//         }
					//         const questionControl = question.id;

					//         if (question.detail_type == 'check' || question.detail_type == 'check-price') {
					//           if (question.required) {
					//             this.bookingQuestionsForm.addControl(questionControl, new FormControl(false, Validators.requiredTrue));
					//           } else {
					//             this.bookingQuestionsForm.addControl(questionControl, new FormControl(false));
					//           }
					//         } else {
					//           if (question.required) {
					//             this.bookingQuestionsForm.addControl(questionControl, new FormControl('', Validators.required));
					//           } else {
					//             this.bookingQuestionsForm.addControl(questionControl, new FormControl(''));
					//           }
					//         }
					//       }
					//     }
					//   }
					// }
				});

				this.apiService
					.getConfig(AppConfig.settings.core.company_id)
					.then((config) => {
						const slotDate = new Date(this.datastoreService.selectSlot.start);
						const now = new Date();

						const twelveMonthsAhead = new Date();
						twelveMonthsAhead.setFullYear(now.getFullYear() + 1);

						this.noticeGuidance =
							slotDate >= now && slotDate <= twelveMonthsAhead
								? config['journey_config']?.['ceremonies']?.['page_content']?.[
								'default'
								]?.['booking_details_page_next_page_notice_guidance'] || ''
								: '';

						this.continueGuidance =
							config['journey_config']?.['ceremonies']?.['page_content']?.[
							'default'
							]?.['booking_details_page_next_page_guidance'];
					});
			}
		});
		this.spinner.hide();
	}

	ngAfterViewInit(): void {
		new PasswordInput(this.passwordCtrl.nativeElement);
	}

	forceRecreateFormBuilder() {
		this.showFormBuilder = false;
		setTimeout(() => (this.showFormBuilder = true));
	}

	updateAnswers(event: any) {
		// Handle service lookup

		if (event.formData) {
			this.commonFunctions.updateAnswers(event);
		}
	}

	storeSchema(schema: any) {
		this.commonFunctions.storeSchema(schema);
	}

	async radioCopyChange() {
		const status = this.customerDetailsForm.value.copyData;
		if (this.datastoreService.config.journey_config.copyClientDetails) {
			const copyClientDetails =
				this.datastoreService.config.journey_config.copyClientDetails;
			if (status === 'Yes') {
				for (let i in copyClientDetails.from) {
					this.companyQuestionsForm.controls[
						copyClientDetails.to[i].toString()
					].setValue(this.customerDetailsForm.value[copyClientDetails.from[i]]);
				}
			} else {
				for (let i in copyClientDetails.to) {
					this.companyQuestionsForm.controls[
						copyClientDetails.to[i].toString()
					]?.setValue('');
				}
			}
		}
	}

  formReady($event: Record<string, any>) {
    console.log('Form ready event received in BookingDetailsComponent:', $event);
    if ($event.form_id === this.selectedFormId) {
      console.log('Extra items after form ready event:', $event.extra_items);
      const fees = $event.extra_items;
      console.log('Adding extra item fees from form ready event:', this.datastoreService.serviceFees);
      if (!this.datastoreService.serviceFees) {
        this.datastoreService.serviceFees = [];
      }
      console.log(this.datastoreService._extraItems);
      for (const fee of this.datastoreService._extraItems) {
        const found = this.datastoreService.serviceFees.findIndex((item: any) => item.questionid === fee.field);
        console.log('Checking if fee already exists for', fee.field, 'Found at index:', found);
        if (found === -1) {
          const restored = {
            questionid: fee.field,
            item: fee.item_name,
            quantity: fee.qty,
            cost: fee.item_price,
            // without this, coming back to this page lost the link id again
            item_id: fee.item_id,
          };
          this.costBreakDown.push(restored);
          this.datastoreService.serviceFees.push({ ...restored });
        }
      }
      console.log('Updated service fees in datastore:', this.datastoreService.serviceFees);
    }
  }

  async onExtraItemSet($event: Record<string, any>) {
    console.log(`Extra item set event received in BookingDetailsComponent: `, $event);
    if ($event.item) {
      console.log(`Adding extra item fee for extra item: `, $event.item);
      this.datastoreService._extraItems.push($event.item);
      this.costBreakDown.push({
        questionid: $event.item.field,
        item: $event.item.item_name,
        quantity: $event.item.qty,
        cost: $event.item.item_price,
      });
      this.datastoreService.triggerAddFees({
        questionid: $event.item.field,
        item: $event.item.item_name,
        quantity: $event.item.qty,
        cost: $event.item.item_price,
        item_id: $event.item.item_id,
      });
    }
    else {
      // Item was deselected (quantity set to 0) — remove it from tracked items and fees
      const itemId = $event.item_id;
      const extraIdx = this.datastoreService._extraItems.findIndex((item: any) => item.item_id === itemId);
      if (extraIdx !== -1) {
        const removedItem = this.datastoreService._extraItems[extraIdx];
        this.datastoreService._extraItems.splice(extraIdx, 1);
        if (this.datastoreService.serviceFees) {
          this.datastoreService.triggerAddFees({
            questionid: removedItem.field,
            item: removedItem.item_name,
            quantity: 0,
            cost: 0,
            item_id: itemId,
          });
        }
      }
    }
    console.log('this.datastoreService._extraItems', this.datastoreService._extraItems);
    console.log('this.datastoreService.serviceFees', this.datastoreService.serviceFees);
    console.log('this.costBreakDown', this.costBreakDown);
  }

  // The template binds (processing) but nothing implemented it, so every
  // certificate selection threw "onFormProcessing is not a function" and the
  // fetch ran with no spinner — the customer could Continue mid-fetch and lose
  // the fee. The library emits true around the extra-item fetch, false when done.
  onFormProcessing($event: boolean) {
    if ($event) {
      this.spinner.show();
    } else {
      this.spinner.hide();
    }
  }

  async onExtraItemChanged($event: Record<string, any>) {
    console.log(`Extra item changed event received in BookingDetailsComponent: `, $event);
    if ($event.item) {
      // Match on the question, not the item id: the tracked entry and the change
      // payload both carry `field`, and only this question's row must move. The old
      // comparison read $event.item_id (one level too high), so it never matched and
      // the whole body was skipped.
      const foundIndex = this.datastoreService._extraItems.findIndex(
        (item: any) => item.field === $event.item.field
      );
      if (foundIndex !== -1) {
        this.datastoreService._extraItems.splice(foundIndex, 1);
      }
      this.datastoreService._extraItems.push($event.item);

      const fee = {
        questionid: $event.item.field,
        item: $event.item.item_name,
        quantity: $event.item.qty,
        cost: $event.item.item_price,
        item_id: $event.item.item_id,
      };
      // Replace this question's breakdown row. Pushing left the old quantity
      // alongside the new one.
      const rowIndex = this.costBreakDown.findIndex(
        (row: any) => row.questionid === fee.questionid
      );
      if (rowIndex !== -1) {
        this.costBreakDown.splice(rowIndex, 1, fee);
      } else {
        this.costBreakDown.push(fee);
      }
      this.datastoreService.triggerAddFees(fee);
    }
    console.log('this.datastoreService._extraItems', this.datastoreService._extraItems);
    console.log('this.datastoreService.serviceFees', this.datastoreService.serviceFees);
    console.log('this.costBreakDown', this.costBreakDown);
  }

	async onCompanySet($event: string) {
		console.log(`Location set event received in BookingDetailsComponent: ${$event}`);
	}

	async onServiceSet($event: string) {
	}

	async onResourceSet($event: string) {
		console.log(`Resource set event received in BookingDetailsComponent: ${$event}`);
	}

	async onStaffSet($event: string) {
		console.log(`Staff set event received in BookingDetailsComponent: ${$event}`);
	}

	async onAddDuration($event: string) {
		console.log(`Add duration event received in BookingDetailsComponent: ${$event}`);
		this.datastoreService.extraDuration =
			Number(this.datastoreService.extraDuration) +
			Number($event);
		console.log(
			`Adding duration: ${$event} - total extra is now ${this.datastoreService.extraDuration}`
		);
	}

	async updateDatastore($event: any, field: string) {
		console.log(`Update datastore event received in BookingDetailsComponent: ${$event} for field ${field}`);
		this.datastoreService[field] = $event;
	}

	async onAddressSet($event: any, field) {
		console.log(`Address set event received in BookingDetailsComponent:`, $event);
		const selectedAddress = $event
		this.datastoreService.selectedAddress = selectedAddress;
	}

	decodeHtmlEntities(text) {
		const entities = {
			'&amp;': '&',
			'&lt;': '<',
			'&gt;': '>',
			'&quot;': '"',
			'&#39;': "'",
		};
		return text.replace(
			/&amp;|&lt;|&gt;|&quot;|&#39;/g,
			(match) => entities[match]
		);
	}

	addHyperlinks(text: string): string {
		return text.replace(/~(.*?)~/g, (match, p1) => {
			const decoded = this.decodeHtmlEntities(p1);
			return `<a ${decoded}</a>`;
		});
	}

	onQuestionChange() {
		// this.showServices = false;
		this.datastoreService.extraDuration = 0; // reset or we add more on everytime
		if (!this.companyQuestions) {
			return;
		}
		const bookingQuestions = this.companyQuestions;
		// Conditional questions logic
		// for (let index = 0; index < bookingQuestions.length; index++) {
		bookingQuestions.forEach(async (bookingQuestion: any) => {
			bookingQuestion.visible = false; // reset any questions that shouldnt be visible
			// go get the answer for every question from the form
			let control = this.companyQuestionsForm.get(`${bookingQuestion.id}`);
			bookingQuestion.answer = undefined;
			if (control) {
				// set booking question from form values
				bookingQuestion.answer = control.value ? control.value : undefined;
			}

			if (bookingQuestion['help_text']) {
				bookingQuestion.conditionals = JSON.parse(bookingQuestion['help_text']); // to change to settings block soon

				if (bookingQuestion.conditionals.hasOwnProperty('help_text')) {
					bookingQuestion.helptext = bookingQuestion.conditionals.help_text;
					let linkPattern = /#(.*?)#/g;
					if (linkPattern.test(bookingQuestion.helptext)) {
						bookingQuestion.helptext = bookingQuestion.helptext.replace(
							linkPattern,
							function (match, p1) {
								return (
									`<a href="${bookingQuestion.conditionals.hyperlink}" target="_blank">` +
									p1 +
									'</a>'
								);
							}
						);
					}

					let tildePattern = /~(.*?)~/g;
					if (tildePattern.test(bookingQuestion.helptext)) {
						bookingQuestion.helptext = bookingQuestion.helptext.replace(
							tildePattern,
							(match, p1) => {
								const decoded = this.decodeHtmlEntities(p1);
								return `<a ${decoded}</a>`;
							}
						);
					}
				}
				if (
					bookingQuestion.conditionals.hasOwnProperty('question-font-colour')
				) {
					bookingQuestion.customLabelColour =
						bookingQuestion.conditionals['question-font-colour'];
				}
				if (bookingQuestion.conditionals.hasOwnProperty('address_lookup')) {
					if (bookingQuestion.conditionals['address_lookup'] === 'yes') {
						bookingQuestion.address_lookup = true;
					}
				}

				if (
					bookingQuestion.conditionals.hasOwnProperty(
						'address_lookup_multi_line'
					)
				) {
					bookingQuestion.address_lookup_multi_line = true;
					bookingQuestion.address_lookup_multi_line_to =
						bookingQuestion.conditionals.address_lookup_multi_line.to;
				}
				if (bookingQuestion.conditionals.hasOwnProperty('validation')) {
					if (!this.config.validation) {
						// if there is no validation on the root
						// its probably under the journey_config
						this.config.validation =
							this.config.journey_config?.appointments?.validation;
					}
					if (this.config.validation[bookingQuestion.conditionals.validation]) {
						bookingQuestion.validation =
							this.config.validation[bookingQuestion.conditionals.validation];
						bookingQuestion.validation_message =
							bookingQuestion.conditionals.validation_message;
						let validators = [Validators.pattern(bookingQuestion.validation)];
						if (bookingQuestion.required) {
							validators.push(Validators.required);
						}
						control.setValidators(validators);
						this.companyQuestionsForm.updateValueAndValidity();
					} else {
						if (bookingQuestion.conditionals.validation == 'email') {
							bookingQuestion.validation = 'email';
							bookingQuestion.validation_message =
								bookingQuestion.conditionals.validation_message;
							let validators = [Validators.email];
							if (bookingQuestion.required) {
								validators.push(Validators.required);
							}
							control.setValidators(validators);
							this.companyQuestionsForm.updateValueAndValidity();
						}
						if (bookingQuestion.conditionals.validation == 'postcode') {
							bookingQuestion.validation = 'postcode';
							bookingQuestion.validation_message =
								bookingQuestion.conditionals.validation_message;
							let validators = [postcodeValidator()];
							if (bookingQuestion.required) {
								validators.push(Validators.required);
							}
							control.setValidators(validators);
							this.companyQuestionsForm.updateValueAndValidity();
						}
					}
				} else {
					if (bookingQuestion.required) {
						control.setValidators(Validators.required);
					}
				}
				if (bookingQuestion.conditionals.hasOwnProperty('section_title')) {
					if (bookingQuestion.conditionals.section_title) {
						bookingQuestion.detail_type = 'heading_2';
					}
				}

				if (bookingQuestion.detail_type === 'date') {
					bookingQuestion.hide_dates_in_future = false;
					bookingQuestion.hide_dates_in_past = false;

					if (
						bookingQuestion.conditionals.hasOwnProperty(
							'hide_dates_in_future'
						) &&
						bookingQuestion.conditionals.hide_dates_in_future
					) {
						bookingQuestion.hide_dates_in_future =
							moment().format('YYYY-MM-DD');
						// future date
						if (bookingQuestion.hide_dates_in_future) {
							let validators = [];
							if (bookingQuestion.required) {
								validators.push(Validators.required);
							}
							validators.push(this.futureDateValidator);
							control.setValidators(validators);
							control.updateValueAndValidity();
							this.companyQuestionsForm.updateValueAndValidity();
						}
					}

					if (
						bookingQuestion.conditionals.hasOwnProperty('hide_dates_in_past') &&
						bookingQuestion.conditionals.hide_dates_in_past
					) {
						bookingQuestion.hide_dates_in_past = moment().format('YYYY-MM-DD');

						// past date
						if (bookingQuestion.hide_dates_in_past) {
							let validators = [];
							if (bookingQuestion.required) {
								validators.push(Validators.required);
							}
							validators.push(this.pastDateValidator);
							control.setValidators(validators);
							control.updateValueAndValidity();
							this.companyQuestionsForm.updateValueAndValidity();
						}
					}
				}

				let conditionals = bookingQuestion.conditionals;
				let show = conditionals.show;
				let hide = conditionals.hide;
				let admin_show = conditionals.admin_show;
				let admin_only = conditionals.admin_only;
				let custom = conditionals.custom;

				// if the booking is only for the admin journey
				if (admin_only && this.datastoreService.adminJourney) {
					show = admin_show;
					admin_only = false;
				}

				if (
					Object.keys(show).length === 0 &&
					Object.keys(hide).length === 0 &&
					!conditionals.admin_only
				) {
					// no hide or show conditional values, just show the question by default
					bookingQuestion.visible = true;
				}

				// show condition loop
				for (const answerId in show) {
					if (answerId == 'hide') {
						return; // this shouldnt happen only if the setup is wrong
					}
					if (answerId === 'custom') {
						const conditions = show[answerId];

						for (const [questionKey, condition] of Object.entries(conditions)) {
							const conditionalQuestion =
								this.companyQuestionsForm.get(questionKey);

							if (
								conditionalQuestion &&
								conditionalQuestion.value != null &&
								((typeof conditionalQuestion.value === 'string' &&
									conditionalQuestion.value.trim().length > 0) ||
									(Array.isArray(conditionalQuestion.value) &&
										conditionalQuestion.value.length > 0))
							) {
								console.log(
									`Evaluating condition for question ${questionKey}:`,
									condition
								);

								const isOrCondition =
									typeof condition === 'string' && condition.includes('OR');
								const isVisible = isOrCondition
									? this.evaluateORCondition(
										condition,
										conditionalQuestion.value
									)
									: this.evaluateCondition(
										condition,
										conditionalQuestion.value
									);

								if (isVisible) {
									console.log(
										`%cCondition met for question ${questionKey}, setting visibility to true`,
										'color: green;'
									);
									bookingQuestion.visible = true;
									break; // stop evaluating once one condition passes
								}
							}
						}
					} else if (answerId == 'custom_multiple_condition') {
						// https://bookinglab.atlassian.net/browse/RWRSPI-376
						console.log(
							'checking show custom_multiple_condition',
							show[answerId]
						);
						const conditionSet = show[answerId]?.conditions;

						if (Array.isArray(conditionSet)) {
							let allConditionsMet = true;

							for (const conditionEntry of conditionSet) {
								const [questionKey, expectedValue] =
									Object.entries(conditionEntry)[0];
								const conditionalQuestion =
									this.companyQuestionsForm.get(questionKey);
								const actualValue = conditionalQuestion?.value;

								const isValuePresent =
									actualValue != null &&
									((typeof actualValue === 'string' &&
										actualValue.trim().length > 0) ||
										(Array.isArray(actualValue) && actualValue.length > 0));

								if (expectedValue === '*') {
									if (!isValuePresent) {
										allConditionsMet = false;
										break;
									}
								} else {
									if (
										!isValuePresent ||
										(typeof actualValue === 'string' &&
											actualValue !== expectedValue) ||
										(Array.isArray(actualValue) &&
											!actualValue.includes(expectedValue))
									) {
										allConditionsMet = false;
										break;
									}
								}
							}

							if (allConditionsMet) {
								console.log(
									`%cAll conditions for id ${bookingQuestion.id} met`,
									'color: green;'
								);
								bookingQuestion.visible = true;
							}
						}
					} else if (answerId === 'custom_or_condition') {
						// https://bookinglab.atlassian.net/browse/RWRSPI-382
						console.log('checking show custom_or_condition', show[answerId]);
						const conditionSet = show[answerId]?.conditions;

						if (Array.isArray(conditionSet)) {
							for (const conditionEntry of conditionSet) {
								const [questionKey, acceptedAnswers] = Object.entries(
									conditionEntry
								)[0] as any;

								const conditionalQuestion =
									this.companyQuestionsForm.get(questionKey);
								const actualValue = conditionalQuestion?.value;

								if (
									actualValue != null &&
									((typeof actualValue === 'string' &&
										acceptedAnswers.includes(actualValue)) ||
										(Array.isArray(actualValue) &&
											actualValue.some((val) => acceptedAnswers.includes(val))))
								) {
									console.log(
										`%cCondition for custom_or_condition met for question ${questionKey} with value: ${actualValue}`,
										'color: green;'
									);
									bookingQuestion.visible = true;
									break; // One match is enough
								}
							}
						}
					} else {
						// if this is an admin journey, and there is an admin show value
						if (this.datastoreService.adminJourney && admin_show) {
							// if we find a show in both
							if (admin_show[`${answerId}`] && show[`${answerId}`]) {
								// set the show to the admin version as it supersedes show
								show[`${answerId}`] = admin_show[`${answerId}`];
								bookingQuestion.admin_override = true;
							}
						}

						let conditionalQuestion = this.companyQuestionsForm.get(
							`${answerId}`
						);
						if (!conditionalQuestion && show) {
							if (this.datastoreService.startQuestions) {
								conditionalQuestion = this.datastoreService.startQuestions.get(
									`${answerId}`
								);
							}
						}

						if (conditionalQuestion && conditionalQuestion.value != null) {
							if (
								conditionalQuestion.value ||
								conditionalQuestion.value.length > 0
							) {
								if (show[answerId].trim() == '*') {
									// wild card
									if (
										conditionalQuestion.value ||
										conditionalQuestion.value.length > 0
									) {
										// if there is any answer
										bookingQuestion.visible = true;
									}
								} else {
									if (`${conditionalQuestion.value}` == `${show[answerId]}`) {
										bookingQuestion.visible = true;
									}
								}
							}
						}
					}
				}

				// perform hide after show because we want it to take priority
				for (const answerId in hide) {
					if (answerId == 'show') {
						return; // this shouldnt happen only if the setup is wrong
					}
					if (answerId == 'custom') {
						let conditionalQuestion = this.companyQuestionsForm.get(
							`${Object.keys(hide[answerId])[0]}`
						);
						if (conditionalQuestion && conditionalQuestion.value != null) {
							if (
								conditionalQuestion.value ||
								conditionalQuestion.value.length > 0
							) {
								let condition: any = Object.values(hide[answerId])[0];
								if (condition.includes('OR')) {
									if (
										this.evaluateORCondition(
											condition,
											conditionalQuestion.value
										)
									) {
										bookingQuestion.visible = true;
									}
								} else {
									if (
										this.evaluateCondition(condition, conditionalQuestion.value)
									) {
										bookingQuestion.visible = true;
									}
								}
							}
						}
					} else {
						const conditionalQuestion = this.companyQuestionsForm.get(
							`${answerId}`
						);
						if (conditionalQuestion && conditionalQuestion.value != null) {
							if (
								conditionalQuestion.value ||
								conditionalQuestion.value.length > 0
							) {
								if (show[answerId] == '*') {
									// wild card
									if (
										conditionalQuestion.value ||
										conditionalQuestion.value.length > 0
									) {
										// if there is any answer
										bookingQuestion.visible = true;
									}
								} else {
									if (`${conditionalQuestion.value}` == `${show[answerId]}`) {
										bookingQuestion.visible = false;
									}
								}
							}
						}
					}
				}

				// If booking question should be displayed in themake-it-yours
				// enhanced price breakdown component, rempve from company questions
				// and add to enhanced cost breakdown questions
				if (
					bookingQuestion.conditionals.hasOwnProperty(
						'fees-and-make-it-yours-enhancements'
					)
				) {
					if (
						bookingQuestion.conditionals[
						'fees-and-make-it-yours-enhancements'
						] === true ||
						bookingQuestion.conditionals[
						'fees-and-make-it-yours-enhancements'
						] === 'true'
					) {
						this.enhancedCostBreakdownQuestions = [
							...this.enhancedCostBreakdownQuestions,
							this.mapToEnhancedCostBreakdownQuestion(bookingQuestion),
						];
					}
				}

				// copy question schema
				if (bookingQuestion.visible) {
					if (bookingQuestion.conditionals.hasOwnProperty('copy_data')) {
						bookingQuestion.copy_data_from =
							bookingQuestion.conditionals['copy_data'].from;
						bookingQuestion.copy_data_to =
							bookingQuestion.conditionals['copy_data'].to;
						bookingQuestion.copy_answer =
							bookingQuestion.conditionals['copy_data'].answer;

						if (
							bookingQuestion.answer == true ||
							bookingQuestion.copy_answer == bookingQuestion.answer
						) {
							bookingQuestion.copy_data_from.forEach((fromId, index) => {
								const toId = bookingQuestion.copy_data_to[index];
								const sourceControl = this.companyQuestionsForm.get(
									String(fromId)
								);
								const targetControl = this.companyQuestionsForm.get(
									String(toId)
								);
								if (sourceControl && targetControl) {
									targetControl.setValue(sourceControl.value);
								} else {
									console.warn(
										`Control not found for: ${fromId} or target: ${toId}`
									);
								}
							});
						}
						// commented out because we cant clear the fields if there is no answer because
						// the field will be cleard everytime the user changes an answer but hasnt filled in the copy question
						// else {
						//   // clear all the copy_data_to fields
						//   bookingQuestion.copy_data_to.forEach((toId) => {
						//     const targetControl = this.companyQuestionsForm.get(String(toId));
						//     if (targetControl) {
						//       targetControl.setValue('');
						//     }
						//   });
						// }
					}
				}

				// add hidden field
				if (bookingQuestion.conditionals.hasOwnProperty('hidden_field')) {
					bookingQuestion.hidden = true;
				}

				// add duration schema
				if (bookingQuestion.visible) {
					if (bookingQuestion.conditionals.hasOwnProperty('add_duration')) {
						this.datastoreService.extraDuration =
							Number(this.datastoreService.extraDuration) +
							Number(bookingQuestion.conditionals.add_duration);
					}
				}

				// set sevice question to var
				if (bookingQuestion.visible) {
					if (bookingQuestion.conditionals.hasOwnProperty('service_set')) {
						this.datastoreService.setService =
							bookingQuestion.conditionals.service_set;
					}
				}

				// extra booking fee item
				if (bookingQuestion.visible) {
					this.addExtraBookingItemFee(bookingQuestion);
				}

				// set client data on booking details page
				if (bookingQuestion.visible) {
					if (
						bookingQuestion.conditionals.hasOwnProperty('booker_first_name')
					) {
						this.datastoreService.booker_first_name = bookingQuestion.id;
					}
					if (bookingQuestion.conditionals.hasOwnProperty('booker_last_name')) {
						this.datastoreService.booker_last_name = bookingQuestion.id;
					}
					if (bookingQuestion.conditionals.hasOwnProperty('booker_email')) {
						this.datastoreService.booker_email = bookingQuestion.id;
					}
					if (bookingQuestion.conditionals.hasOwnProperty('booker_phone')) {
						this.datastoreService.booker_phone = bookingQuestion.id;
					}
				}

				// if (bookingQuestion.conditionals.hasOwnProperty('continuebutton')) {
				//   // showContinueButton
				//   let conditionalQuestion = this.companyQuestionsForm.get(`${Object.keys(bookingQuestion.conditionals.continuebutton)[0]}`);
				//   console.log(conditionalQuestion);
				//   if (conditionalQuestion && conditionalQuestion.value != null) {
				//     if ((conditionalQuestion.value || conditionalQuestion.value.length > 0)) {
				//       if (Object.values(bookingQuestion.conditionals.continuebutton)[0][0] == "*") { // wildcard
				//         if ((conditionalQuestion.value || conditionalQuestion.value.length > 0)) {
				//           if (this.config["auto_select_service"]) {
				//             this.showContinueButton = true;
				//           } else {
				//             // filter services
				//             if (this.datastoreService.setService) {
				//               this.filteredServiceList = this.servicesList.filter(service => `${service.id}` == `${this.datastoreService.setService}`);
				//             } else {
				//               this.filteredServiceList = this.servicesList;
				//             }
				//             // this.showServices = true;
				//           }
				//         }
				//       } else {
				//         if (conditionalQuestion.value == Object.values(bookingQuestion.conditionals.continuebutton)[0][0]) {
				//           if (this.config["auto_select_service"]) {
				//             this.showContinueButton = true;
				//           } else {
				//             // filter services
				//             if (this.datastoreService.setService) {
				//               this.filteredServiceList = this.servicesList.filter(service => `${service.id}` == `${this.datastoreService.setService}`);
				//             } else {
				//               this.filteredServiceList = this.servicesList;
				//             }
				//             // this.showServices = true;
				//           }
				//         }
				//       }
				//     }
				//   }
				// }

				// slice booking questions off for first-n
				if (bookingQuestion.visible && !bookingQuestion.optionsRemoved) {
					if (
						bookingQuestion.conditionals.hasOwnProperty(
							'remove-options-customer'
						)
					) {
						let firstN =
							bookingQuestion.conditionals['remove-options-customer'][
							'first-n'
							];
						if (!this.datastoreService.adminJourney) {
							bookingQuestion.options = bookingQuestion.options.slice(firstN);
							bookingQuestion.optionsRemoved = true;
						}
					}
				}

				if (!this.datastoreService.adminJourney && admin_only) {
					bookingQuestion.visible = false;
				}

				if (!bookingQuestion.original_name) {
					bookingQuestion.original_name = bookingQuestion.name; // store old name
				}

				// if question name contains braces
				if (
					bookingQuestion.original_name && // original name for updates because first iteration removes braces
					bookingQuestion.original_name.includes('{') &&
					bookingQuestion.original_name.includes('}')
				) {
					console.log(bookingQuestion);

					bookingQuestion.name = bookingQuestion.original_name.replace(
						/{(.*?)}/g,
						(match, refKey) => {
							const refQuestion = bookingQuestions.find(
								(q) => q.question_reference == refKey
							);

							if (refQuestion) {
								if (refQuestion.answer) {
									console.log(
										`Reference question ${refKey} found with answer:`,
										refQuestion.answer
									);
									return refQuestion.answer;
								} else {
									console.warn(
										`Reference question ${refKey} has no answer, using placeholder.`
									);
									return match;
								}
							} else {
								console.warn(`Reference question ${refKey} not found`);
								return match;
							}
						}
					);
				}

				if (!bookingQuestion.visible) {
					let conditionalQuestion = this.companyQuestionsForm.get(
						`${bookingQuestion.id}`
					);
					conditionalQuestion.reset();
					conditionalQuestion.clearValidators();
					conditionalQuestion.updateValueAndValidity();
				}
			} else {
				bookingQuestion.visible = true;
			}
		});

		// remove cost enhancement questions from company questions
		for (const question of this.enhancedCostBreakdownQuestions) {
			const index = this.companyQuestions.indexOf(question);

			if (index > -1) {
				this.companyQuestions.splice(index, 1);
			}
		}
	}

	evaluateORCondition(condition, answer) {
		const parts = condition.split(' OR ');

		let evaluation = [];
		// Evaluate each part of the condition
		for (const part of parts) {
			if (part == answer) {
				evaluation.push(true);
			} else {
				evaluation.push(false);
			}
		}

		console.log(evaluation);
		let evalArray = evaluation.some((element) => element === true);
		// If evaluation has a true, one of the values is correct so it will return true
		return evalArray;
	}

	// Function to evaluate custom condition
	evaluateCondition(condition, answer) {
		// Split the condition into individual parts
		const parts = condition.split(' and ');

		// Evaluate each part of the condition
		for (const part of parts) {
			// Split the part into operator and threshold
			let [operator, thresholdStr] = part.split(' ');
			const threshold = parseFloat(thresholdStr);

			// decode html entity
			operator = he.decode(operator);

			if (this.isValidDate(answer)) {
				answer = moment(answer).diff(moment(), 'days') + 1;
			}

			// Perform the comparison based on the operator
			switch (operator) {
				case '<':
					if (!(parseFloat(answer) < threshold)) {
						return false; // If any part is false, the entire condition is false
					}
					break;
				case '<=':
					if (!(parseFloat(answer) <= threshold)) {
						return false;
					}
					break;
				case '>':
					if (!(parseFloat(answer) > threshold)) {
						return false;
					}
					break;
				case '>=':
					if (!(parseFloat(answer) >= threshold)) {
						return false;
					}
					break;
				case '=':
					if (!(parseFloat(answer) === threshold)) {
						return false;
					}
					break;
				default:
					return false;
			}
		}

		// If all parts pass, return true
		return true;
	}

	validateQuestions(event, question) {
		// Extract the day, month, and year values from the inputs
		const dayInput = (
			document.getElementById(`${question.id}_d`) as HTMLInputElement
		)?.value;
		const monthInput = (
			document.getElementById(`${question.id}_m`) as HTMLInputElement
		)?.value;
		const yearInput = (
			document.getElementById(`${question.id}_y`) as HTMLInputElement
		)?.value;

		// Combine them into a single date string
		const day = dayInput?.padStart(2, '0'); // Ensure day is two digits
		const month = monthInput?.padStart(2, '0'); // Ensure month is two digits
		const year = yearInput; // Year doesn't need padding

		// Validate that the inputs are valid and combine into a date
		if (day && month && year) {
			this.companyQuestionsForm.controls[question.reference].markAsTouched();
			this.companyQuestionsForm.controls[question.reference].markAsDirty();
			const combinedDate = `${year}-${month}-${day}`; // Format: YYYY-MM-DD
			if (this.isValidDateQuestion(combinedDate)) {
				console.log(combinedDate);
				this.companyQuestionsForm.controls[question.reference].setValue(
					combinedDate
				);
				console.log(this.companyQuestionsForm);
			} else {
				console.error('Invalid date entered:', combinedDate);
				// question.selectedDate = null; // Clear selectedDate if invalid
			}
		} else {
			question.selectedDate = null; // Clear selectedDate if not all fields are filled
		}

		// this.calculateVisibility();
		this.onQuestionChange();
	}

	isValidDateQuestion(dateString: string): boolean {
		const date = new Date(dateString);
		return !isNaN(date.getTime()); // Check if the date is valid
	}

	updateRadio(form, question, answer) {
		console.log(form, question, answer);
		form.controls[question].setValue(answer);
		if (question !== 'copyData') {
			this.onQuestionChange();
		} else {
			this.radioCopyChange();
		}
	}

	setAddress(event, question) {
		// set the address value on the form
		const selectElement = event.target as HTMLSelectElement;
		const selectedValue = selectElement.value; // This now gives you `option.id`
		console.log('Selected Value:', selectedValue);
		this.companyQuestionsForm.controls[question.reference].setValue(
			selectedValue
		);
		console.log(this.companyQuestionsForm);
	}

	getAddress(question) {
		question.currently_searching = true;
		// Custom error handling for postcode
		try {
			clearTimeout(this.debounceTimer);
			this.debounceTimer = setTimeout(async () => {
				let postcode: string = this.companyQuestionsForm.controls[
					'postcode_' + question.reference
				].value.replace(/\s/g, '');
				if (postcode.length > 1) {
					//check postcode accepts regex before calling
					if (this.postcodeValidator.test(postcode)) {
						this.apiService.get(postcode).subscribe(
							(res) => {
								this.companyQuestionsForm.controls[
									`postcode_${question.id}`
								].setErrors({ invalid_address: false });
								var object: any = res;
								if (object.results) {
									var addresses = object.results;
									question.addresses = [];
									if (addresses.length > 1) {
										// If it matches up, push the addresses
										addresses.forEach((item) => {
											console.log(item);
											question.addresses.push({ address: item });
										});
									}
								}
								this.spinner.hide();
							},
							(err) => {
								console.log(err);
								this.spinner.hide();
							}
						);
					} else {
						this.companyQuestionsForm.controls[
							`postcode_${question.id}`
						].setErrors({ invalid_address: true });
					}
				}
			}, 500);
		} catch (error) {
			console.log(error);
		}
	}

	onFileChange(event, questionId) {
		if (event.target.files.length > 0) {
			const file = event.target.files[0];
			this.form[questionId].setValue(file, { emitModelToViewChange: false });
			this.bookingQuestionsForm.patchValue({
				fileSource: file,
			});
		}
	}

	isValidDate(dateString: string): boolean {
		// Attempt to create a Date object from the dateString
		const date: Date = new Date(dateString);
		// Check if the created Date object is valid and its string representation matches the input
		return (
			date instanceof Date &&
			!isNaN(date.getTime()) &&
			date.toISOString().slice(0, 10) === dateString
		);
	}

	getAge(dateString): Number {
		var today = new Date();
		var birthDate = new Date(dateString);
		var age = today.getFullYear() - birthDate.getFullYear();
		var m = today.getMonth() - birthDate.getMonth();
		if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
			age--;
		}
		return age;
	}

	// validate future dates
	futureDateValidator(control) {
		const currentDate = new Date();
		const inputDate = new Date(control.value);
		if (control.value) {
			if (inputDate > currentDate) {
				return { futureDate: true };
			}
			return null;
		} else {
			return null;
		}
	}

	// validate past dates
	pastDateValidator(control) {
		const currentDate = new Date();
		const inputDate = new Date(control.value);
		if (control.value) {
			if (inputDate < currentDate) {
				return { pastDate: true };
			}
			return null;
		} else {
			return null;
		}
	}

	async onSubmit() {
		this.spinner.show();
		this.submitted = true;
		this.alertService.clear();

		// Check if form is valid
		// console.log(this.customerDetailsForm.invalid || this.bookingQuestionsForm.invalid)

		// Object.keys(this.companyQuestionsForm.controls).forEach((key) => {
		// 	const control = this.companyQuestionsForm.controls[key];
		// 	//clear postcode validation for postcode search as it will prevent submission on non-mandatory field
		// 	if (key.startsWith('postcode_')) {
		// 		control.setErrors(null);
		// 	}
		// });

		// trigger form validation
		this.customerDetailsForm.markAllAsTouched();
		this.companyQuestionsForm.markAllAsTouched();

		// trigger the validation on the customerDetailsForm
		if (this.showContactForm) {
			this.customerQuestions.forEach((question) => {
				this.customerDetailsForm.controls[question.id].markAsTouched();
			});
		}

		if (this.adminJourney) {
			// trigger the validation on the customerDetailsForm
			if (this.showContactForm) {
				if (
					this.customerDetailsForm.value.email_address_consent == false ||
					this.customerDetailsForm.value.contact_number_consent == false
				) {
					this.scrollToTop();
					this.spinner.hide();
					this.alertService.warn(
						this.translateService.instant('FORM.VALIDATION_ERR_MSG')
					);
					return;
				}
			}
		} else {
			if (this.showContactForm) {
				this.customerDetailsForm.controls.email_address_consent.setValue(true);
				this.customerDetailsForm.controls.contact_number_consent.setValue(true);
				this.customerDetailsForm.controls.email_address_consent.setErrors(null);
				this.customerDetailsForm.controls.contact_number_consent.setErrors(
					null
				);
			}
		}

		if (
			(this.showContactForm && this.customerDetailsForm.invalid) 
		) {
			this.scrollToTop();
			this.spinner.hide();
			this.alertService.warn(
				this.translateService.instant('FORM.VALIDATION_ERR_MSG')
			);
			return;
		}

		// SUP-2485: enforce required fields in the schema form before continuing.
		if (this.formBuilderRef && !this.formBuilderRef.validateAndReveal()) {
			this.scrollToTop();
			this.spinner.hide();
			this.alertService.warn(
				this.translateService.instant('FORM.VALIDATION_ERR_MSG')
			);
			return;
		}

		// var currentDate = new Date(this.slot.start);
		// await this.apiService.checkForDoubleBooking(
		//   this.selectedDepartment,
		//   currentDate,
		//   this.customerDetailsForm.value.email_address ? this.customerDetailsForm.value.email_address : null,
		//   this.customerDetailsForm.value.mobile_number ? this.customerDetailsForm.value.mobile_number : null,
		// ).then((res: []) => {
		//   // res = array of unavailable times
		//   this.unavailableTimes = res

		this.completeBooking();
		// })
	}

	async completeBooking() {
		// this.bookingQuestionsForm.controls.contact(this.datastoreService.startQuestions)
		this.datastoreService.bookingForm = this.bookingQuestionsForm;
		this.datastoreService.companyAnswers = this.companyQuestionsForm;
    console.log(this.datastoreService);


		this.datastoreService.customerDetailsForm = this.customerDetailsForm;

		console.log(
			'b2c',
			this.datastoreService.config.journey_config.hasOwnProperty('b2c'),
			'adminJourney',
			this.adminJourney
		);
		if (this.datastoreService.config.hasOwnProperty('journey_config')) {
			if (
				this.datastoreService.config.journey_config.hasOwnProperty('b2c') &&
				!this.adminJourney
			) {
				if (!this.b2cService.token) {
					const authToken: string = localStorage.getItem('auth_token');
					this.spinner.show();
					try {
						await this.addItem();
					} catch (err) {
						// addItem() has already surfaced the error to the customer. Do not fall through:
						// the service item can still hold whatever resource JRNI auto-assigned, which is
						// exactly how bookings land on the wrong resource.
						console.error('Aborting checkout — service item update failed', err);
						this.spinner.hide();
						return;
					}
					// we need to update the service item here now...
					this.b2cService.sendToAuth(
						this.selectedLocation.id,
						this.selectedService.id,
						this.basketservice.currentBasket.id,
						this.basketservice.currentServiceItem.id,
						authToken,
						this.datastoreService.serviceFees,
						this.datastoreService.makeItYoursPaymentType
					);
				} else {
					this.router.navigate(['/summary']);
				}
			} else {
				this.spinner.hide();
				this.router.navigate(['/summary']);
			}
		} else {
			this.spinner.hide();
			this.router.navigate(['/summary']);
		}
	}

	async addItem() {
		// Get the selected slot from the previous page
		const selectedSlot = await this.availabilityService.getSelectedSlot();

		// Get the booking answers and add them to an array ready to add to basket
		for (const key in this.datastoreService.bookingForm.value) {
			{
				this.bookingAnswers.push({
					id: key,
					answer: this.datastoreService.bookingForm.value[key],
				});
			}
		}
		for (const key in this.datastoreService.companyQuestionsForm.value) {
			{
				const isAuxPostcode = key.startsWith('postcode_') || (key.endsWith('_postcode') && key.slice(0, -9) in this.datastoreService.companyQuestionsForm.value);
				if (!isAuxPostcode) {
					this.bookingAnswers.push({
						id: key,
						answer: this.datastoreService.companyQuestionsForm.value[key],
					});
				}
			}
		}
		for (const key in this.datastoreService.companyAnswers.value) {
			{
				if (!key.includes('postcode')) {
					// ditch any postcode lookups
					this.bookingAnswers.push({
						id: key,
						answer: this.datastoreService.companyAnswers.value[key],
					});
				}
			}
		}

		// const questions = (await this.questionsService.getCompanyQuestions().toPromise())['_embedded']
		// const altQuestions = (await this.questionsService.getAltCompanyQuestions().toPromise())['_embedded']

		// const altQuestions:any = await this.questionsService.getAltCompanyQuestions().toPromise().then(questions => {return questions})
		// add the extra questions from the departments page
		// we do all this because we get the answers to several questions before we know the service
		//so we need to convert the base service's questions too the alt ones, and the only shared identifier (that we can access) is name
		if (this.datastoreService.companyQuestions) {
			for (const key in this.datastoreService.companyQuestionsForm.value) {
				const myQuestion = this.datastoreService.companyQuestions.filter(
					(x) => x.id.toString() === key
				)[0];
				// const myALtQuestion = altQuestions.questions.filter(x => x.name === myQuestion.name)[0]
				if (myQuestion) {
					if (myQuestion.length > 0) {
						this.bookingAnswers.push({
							id: key,
							answer: this.datastoreService.companyQuestionsForm.value[key],
						});
					}
				}
				// if(myALtQuestion !== undefined){
				//   this.bookingAnswers.push({
				//     id: myALtQuestion.id,
				//     answer: this.datastoreService.companyQuestionsForm.value[key],
				//   });
				// } else{
				//   alert("administrative error: Denbigh and Ruthin's question titles must match those of the other companies (this includes both translations)")
				//   return
				// }
			}
		}
		// Is the booking in Welsh, Auto populate this question
		// const usingWelsh = this.translateService.currentLang == "cy" ? 'Welsh' : 'English';
		// this.bookingAnswers.push({ id: 271, answer: usingWelsh })
		// this.bookingAnswers.push({ id: 39, answer: usingWelsh })

		this.datastoreService.bookingAnswers = this.bookingAnswers;

		console.log(this.bookingAnswers);

		// Add item to the basket
		// const res = await this.basketService.updateItemV1(
		//   this.selectedLocation,
		//   this.selectedService.id,
		//   this.selectedService.calculated_duration,
		//   selectedSlot.start,
		//   this.bookingAnswers,
		//   this.datastoreService.client.id,
		//   this.selectedResource
		// )

		if (this.datastoreService.adminJourney) {
			try {
				const updateServiceItem = await this.basketservice.updateItemV1(
					this.selectedLocation,
					this.selectedService.id,
					this.selectedService.calculated_duration,
					selectedSlot.start,
					this.bookingAnswers,
					this.datastoreService.client.id,
					this.selectedResource
				);
				console.log(updateServiceItem);
				// api.service swallows HTTP failures and returns the error message as a STRING
				// instead of throwing, so a failed update reads as success here. Anything that is
				// not a service item means the item still carries the resource JRNI auto-assigned
				// — i.e. the wrong room — so fail loudly rather than paying for it.
				if (!updateServiceItem || typeof updateServiceItem !== 'object') {
					throw new Error(
						`Service item update failed: ${JSON.stringify(updateServiceItem)}`
					);
				}
				return updateServiceItem;
			} catch (err) {
				console.log(1);
				console.error(err);
				this.alertService.error(
					this.translateService.instant('SUMMARY.INVALID_SLOT')
				);
				this.spinner.hide();
				this.scrollToTop();
				throw err;
			}
		} else {
			try {
				console.log('selectedResource', this.selectedResource);
				const updateServiceItem = await this.apiService.updateServiceItem(
					this.selectedLocation,
					this.basketservice.currentBasket,
					this.basketservice.currentServiceItem,
					{
						company_id: this.selectedLocation.id,
						service_id: this.selectedService.id,
						duration: this.selectedService.calculated_duration,
						start: `${selectedSlot.start}`,
						resource_id: this.apiService.resolveResourceId(this.selectedResource),
						questions: this.bookingAnswers,
					}
				);

				console.log(updateServiceItem);
				// api.service swallows HTTP failures and returns the error message as a STRING
				// instead of throwing, so a failed update reads as success here. Anything that is
				// not a service item means the item still carries the resource JRNI auto-assigned
				// — i.e. the wrong room — so fail loudly rather than paying for it.
				if (!updateServiceItem || typeof updateServiceItem !== 'object') {
					throw new Error(
						`Service item update failed: ${JSON.stringify(updateServiceItem)}`
					);
				}
				return updateServiceItem;
			} catch (err) {
				console.log(2);
				console.error(err);
				this.alertService.error(
					this.translateService.instant('SUMMARY.INVALID_SLOT')
				);
				this.spinner.hide();
				this.scrollToTop();
				throw err;
			}
		}
	}

	// Scroll to the top of the page
	scrollToTop() {
		const scrollToOptions = { top: 0, left: 0, behavior: 'instant' };
		window.scrollTo(scrollToOptions as unknown as ScrollToOptions);
	}

	// BRPO-31: apply the per-council phone regex to contact_number.
	applyConfiguredPhoneValidation() {
		const configuredPhone =
			this.config?.validation?.phone ??
			this.config?.journey_config?.ceremonies?.validation?.phone ??
			this.config?.journey_config?.appointments?.validation?.phone;

		const control = this.customerDetailsForm.get('contact_number');
		if (!control) {
			return;
		}

		control.setValidators([
			Validators.required,
			Validators.minLength(9),
			Validators.maxLength(15),
			Validators.pattern(configuredPhone ?? DEFAULT_PHONE_PATTERN),
		]);
		control.updateValueAndValidity({ emitEvent: false });
	}

	// Number input text validation, keycode 8 is backspacev
	keyPressNumberField(event: any) {
		const pattern = /[0-9\+\-\ ]/;
		let inputChar = String.fromCharCode(event.charCode);
		const inputElement = event.target as HTMLInputElement;
		if (
			(event.keyCode != 8 && !pattern.test(inputChar)) ||
			inputElement.value.length >= 15
		) {
			event.preventDefault();
		}
	}

	calculateCertificates(question) {
		const optionChosen = question.options.find(
			(x) =>
				x.name.toString() === this.bookingForm[question.id].value.toString()
		);
		this.costBreakDown.push({
			item: 'Certificates',
			quantity: optionChosen.name,
			cost: optionChosen.price,
		});

		this.datastoreService.costBreakDown = this.costBreakDown;
	}

	// addQuestionsToForm(questions) {
	// 	this.companyQuestions = [];
	// 	this.companyQuestionsForm = this.formBuilder.group({});
	// 	for (let index = 0; index < questions.length; index++) {
	// 		const question = questions[index];
	// 		question.visible = false;
	// 		question.reference = question.id;
	// 		if (!question.admin_only) {
	// 			if (!question.settings.hasOwnProperty('conditional_question')) {
	// 				question.visible = false; // default hidden all
	// 			}

	// 			const questionControl = question.id;

	// 			if (!question['help_text']) {
	// 				// question without any config
	// 				question.conditionals = {
	// 					page: this.router.url, // show on current page
	// 				};
	// 			} else {
	// 				question.conditionals = JSON.parse(question['help_text']);
	// 			}

	// 			if (question.conditionals.page == this.router.url.replace('/', '')) {
	// 				this.companyQuestions.push(question);

	// 				let validators = [];

	// 				// required validator
	// 				if (question.required) {
	// 					validators.push(Validators.required);
	// 				}
	// 				if (
	// 					question.conditionals['address_lookup'] ||
	// 					question.conditionals['address_lookup_multi_line']
	// 				) {
	// 					this.companyQuestionsForm.addControl(
	// 						'postcode_' + questionControl,
	// 						new UntypedFormControl('', validators)
	// 					);
	// 				}
	// 				if (
	// 					question.detail_type == 'check' ||
	// 					question.detail_type == 'check-price'
	// 				) {
	// 					this.companyQuestionsForm.addControl(
	// 						questionControl,
	// 						new UntypedFormControl('', Validators.requiredTrue)
	// 					);
	// 				} else {
	// 					this.companyQuestionsForm.addControl(
	// 						questionControl,
	// 						new UntypedFormControl('', validators)
	// 					);
	// 				}

	// 				// if we can find that question on datastore, we can set value back to what we have saved
	// 				if (this.datastoreService.companyAnswers) {
	// 					let questionFound = this.datastoreService.companyAnswers.get(
	// 						`${question.id}`
	// 					);
	// 					if (questionFound) {
	// 						this.companyQuestionsForm.controls[question.id].setValue(
	// 							questionFound.value
	// 						);
	// 						this.companyQuestionsForm.controls[question.id].patchValue(
	// 							questionFound.value
	// 						);
	// 					}
	// 				}

	// 				// once the controls are added we can set the default values
	// 				if (
	// 					question.detail_type == 'select' ||
	// 					question.detail_type == 'select-price'
	// 				) {
	// 					const defaultOption = question.options.find(
	// 						(option) => option.is_default
	// 					);
	// 					if (defaultOption) {
	// 						this.companyQuestionsForm.controls[question.id].setValue(
	// 							defaultOption.name
	// 						);
	// 						this.companyQuestionsForm.controls[question.id].patchValue(
	// 							defaultOption.name
	// 						);
	// 						this.calculateCertificates(question);
	// 					}
	// 				}
	// 			} else {
	// 				// console.log(`skipped ${question.id}`); // debugging remove later
	// 			}
	// 		}
	// 	}
	// 	this.onQuestionChange();
	// }

	selectSuggestion(selectedUPRN: any, question: any): void {
		const suggestion = question.addresses.find(
			(option) => option.address.UPRN === selectedUPRN
		);
		question.addresses = [];
		question.currently_searching = false;
		if (question.address_lookup_multi_line) {
			console.log(suggestion);
			const address = suggestion['address'];
			this.datastoreService.selectedAddress = suggestion['address'].full_address;
			console.log(this.datastoreService.selectedAddress);
			const multiline = question.address_lookup_multi_line_to;
			// if (address.type == 'DPA') {
			//   const keys = ['BUILDING_NUMBER', 'BUILDING_NAME', 'ORGANISATION_NAME'];
			//   const selectedValue = keys.find((key) => address[key] !== undefined)
			//     ? address[keys.find((key) => address[key] !== undefined)]
			//     : null;
			//   this.companyQuestionsForm.controls[multiline[0]].setValue(
			//     `${selectedValue ?? ''} ${address?.THOROUGHFARE_NAME ?? ''}`
			//   ); // address 1
			//   this.companyQuestionsForm.controls[multiline[1]].setValue(
			//     address?.DEPENDENT_LOCALITY ?? ''
			//   ); // address 2
			//   this.companyQuestionsForm.controls[multiline[2]].setValue(
			//     address?.POST_TOWN ?? ''
			//   ); // address 3
			//   this.companyQuestionsForm.controls[multiline[3]].setValue(
			//     address?.LOCAL_CUSTODIAN_CODE_DESCRIPTION ?? ''
			//   ); // address 4
			//   this.companyQuestionsForm.controls[multiline[4]].setValue(
			//     address?.POSTCODE ?? ''
			//   ); // address 5
			// } else {
			//   this.companyQuestionsForm.controls[multiline[0]].setValue(
			//     `${address.PAO_START_NUMBER ?? ''} ${address.STREET_DESCRIPTION ?? ''
			//     } ${address.SAO_TEXT ?? ''}`
			//   ); // address 1
			//   this.companyQuestionsForm.controls[multiline[1]].setValue(
			//     `${address.LOCALITY_NAME ?? ''} ${address.PAO_TEXT ?? ''}`
			//   ); // address 2
			//   this.companyQuestionsForm.controls[multiline[2]].setValue(
			//     `${address.TOWN_NAME ?? ''}`
			//   ); // address 3
			//   this.companyQuestionsForm.controls[multiline[3]].setValue(
			//     `${address.ADMINISTRATIVE_AREA ?? ''}`
			//   ); // address 4
			//   this.companyQuestionsForm.controls[multiline[4]].setValue(
			//     `${address.POSTCODE ?? ''}`
			//   ); // address 5
			// }
			this.companyQuestionsForm.controls[multiline[0]].setValue(
				`${address.address_line_1 ?? ''}`
			); // address 1
			this.companyQuestionsForm.controls[multiline[1]].setValue(
				`${address.address_line_2 ?? ''}`
			); // address 2
			this.companyQuestionsForm.controls[multiline[2]].setValue(
				`${address.town ?? ''}`
			); // address 3
			this.companyQuestionsForm.controls[multiline[3]].setValue(
				`${address.county ?? ''}`
			); // address 4
			this.companyQuestionsForm.controls[multiline[4]].setValue(
				`${address.postcode ?? ''}`
			); // address 5
			this.onQuestionChange();
		} else {
			// code for single line addresses can go here
			this.companyQuestionsForm.controls[question.id].setValue(
				suggestion['pretty_name']
			);
			console.log('Selected address set:', suggestion['pretty_name']);
			this.datastoreService.selectedAddress = suggestion['pretty_name'];
			this.companyQuestionsForm.controls[question.id].updateValueAndValidity();
			this.onQuestionChange();
		}
	}

	mapToEnhancedCostBreakdownQuestion(question: any) {
		question.detailType = question['detail_type'];

		if (question.conditionals['default']) {
			question.defaultValue = question.conditionals['default'];
		}

		return question;
	}

	async addExtraBookingItemFee(bookingQuestion: any) {
		if (bookingQuestion.conditionals.hasOwnProperty('extra-item')) {
			this.spinner.show();
			bookingQuestion.extraItem = bookingQuestion.conditionals['extra-item'];
			bookingQuestion.extraItemLabel =
				bookingQuestion.conditionals['extra-item-label'];

			let cost;
			let costVar;

			if (bookingQuestion.answer && bookingQuestion.detail_type === 'select') {
				cost = bookingQuestion.options.filter(
					(option) => option.name == bookingQuestion.answer
				);
				costVar =
					cost[0].price > 0 ? cost[0].price / bookingQuestion.answer / 100 : 0;
			} else if (
				bookingQuestion.answer &&
				bookingQuestion.detail_type === 'number'
			) {
				costVar = bookingQuestion.price / bookingQuestion.answer / 100;
			}

			const itemProps: any = await this.apiService.getExtraItemProperties(
				bookingQuestion.extraItem,
				this.selectedDepartment.id,
				this.selectedService.id
			);
			if (itemProps) {
				bookingQuestion.current_price = itemProps.item_price;
			}
			this.costBreakDown.push({
				questionid: bookingQuestion.id,
				item: bookingQuestion.extraItemLabel,
				quantity: bookingQuestion.answer,
				cost: bookingQuestion.current_price || 0,
			});
			this.datastoreService.triggerAddFees({
				questionid: bookingQuestion.id,
				item: bookingQuestion.extraItemLabel,
				quantity: bookingQuestion.answer,
				cost: bookingQuestion.current_price || 0,
			});
			for (const key in this.datastoreService.serviceFees) {
				const question = this.datastoreService.serviceFees[key];
				if (question.questionId) {
					this.bookingAnswers.push({
						id: question.questionId,
						answer: question.answer,
					});
				}
			}
			this.spinner.hide();
		}
	}
}
